import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Transactions.css';
import './RecurringModal.css';

function ExpenseHistoryTable({ householdId }) {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [selectedRecurring, setSelectedRecurring] = useState(null);

  const months = [
    { value: 'all', label: 'All Months' },
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' }
  ];

  const getAvailableYears = () => {
    const allDates = expenses.map(e => new Date(e.date).getFullYear());
    const uniqueYears = [...new Set(allDates)].sort((a, b) => b - a);
    return uniqueYears.length > 0 ? uniqueYears : [new Date().getFullYear()];
  };

  useEffect(() => {
    loadExpenses();
  }, [householdId]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      
      console.log('[ExpenseHistoryTable] Loading expenses for household:', householdId);
      
      // Get household wallets for this household
      const walletsRes = await api.get('/wallets');
      console.log('[ExpenseHistoryTable] All wallets:', walletsRes.data);
      
      const householdWallets = walletsRes.data.filter(w => 
        w.household_id === householdId && w.wallet_type === 'household'
      );
      console.log('[ExpenseHistoryTable] Household wallets:', householdWallets);
      
      const householdWalletIds = householdWallets.map(w => w.id);
      
      // Fetch both household expenses and personal expenses from household wallets
      const [householdExpensesRes, personalExpensesRes, recurringRes, usersRes] = await Promise.all([
        api.get(`/household/${householdId}/expenses`, { params: { limit: 1000 } }),
        api.get('/personal/expenses', { params: { limit: 1000 } }),
        api.get('/recurring'),
        api.get(`/household/${householdId}/members`)
      ]);
      
      console.log('[ExpenseHistoryTable] Household expenses from API:', householdExpensesRes.data);
      console.log('[ExpenseHistoryTable] Personal expenses from API:', personalExpensesRes.data.length, 'total');
      
      // Create lookup maps
      const recurringMap = {};
      recurringRes.data.forEach(rec => {
        recurringMap[rec.id] = rec;
      });
      
      const usersMap = {};
      usersRes.data.forEach(user => {
        usersMap[user.user_id] = user.name;  // Fixed: use user_id not id
      });
      
      console.log('[ExpenseHistoryTable] Users map:', usersMap);
      console.log('[ExpenseHistoryTable] Recurring map:', Object.keys(recurringMap));
      
      // Get household expenses (these have splits)
      const householdExpenses = householdExpensesRes.data.map(expense => ({
        ...expense,
        expense_type: 'household',
        has_splits: true,
        actual_id: expense.id,
        display_id: `household-${expense.id}`
      }));
      
      console.log('[ExpenseHistoryTable] Processed household expenses:', householdExpenses);
      
      // Get personal expenses from household wallets
      const personalExpenses = personalExpensesRes.data
        .filter(expense => householdWalletIds.includes(expense.wallet_id))
        .map(expense => {
          const recurringInfo = expense.recurring_id ? recurringMap[expense.recurring_id] : null;
          return {
            ...expense,
            expense_type: 'personal',
            has_splits: false,
            paid_by_name: 'Personal Expense',
            actual_id: expense.id,
            display_id: `personal-${expense.id}`,
            recurring_info: recurringInfo,
            recurring_creator: recurringInfo ? usersMap[recurringInfo.user_id] : null
          };
        });
      
      console.log('[ExpenseHistoryTable] Filtered personal expenses from household wallets:', personalExpenses);
      
      // Combine and sort by date
      const allExpenses = [...householdExpenses, ...personalExpenses]
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      
      console.log('[ExpenseHistoryTable] Final combined expenses:', allExpenses);
      setExpenses(allExpenses);
    } catch (error) {
      console.error('[ExpenseHistoryTable] Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expense) => {
    const confirmMessage = `Are you sure you want to delete this ${expense.expense_type} expense?\n\n${expense.description} - ${formatCurrency(expense.amount, expense.currency)}\n\nThis action CANNOT be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (expense.expense_type === 'household') {
        await api.delete(`/household/${householdId}/expenses/${expense.actual_id}`);
      } else {
        await api.delete(`/personal/expenses/${expense.actual_id}`);
      }
      loadExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Failed to delete expense');
    }
  };

  const handleViewRecurring = (expense) => {
    if (expense.recurring_info) {
      setSelectedRecurring(expense.recurring_info);
      setShowRecurringModal(true);
    }
  };

  const handleEditRecurring = () => {
    if (selectedRecurring) {
      setShowRecurringModal(false);
      // Navigate to recurring page with the transaction ID
      navigate('/recurring', { state: { editId: selectedRecurring.id } });
    }
  };

  const getFilteredExpenses = () => {
    let filtered = expenses;

    // Filter by month and year
    if (monthFilter !== 'all' && yearFilter !== 'all') {
      filtered = filtered.filter(e => {
        const date = new Date(e.date);
        return date.getMonth() === parseInt(monthFilter) && date.getFullYear() === parseInt(yearFilter);
      });
    } else if (yearFilter !== 'all') {
      filtered = filtered.filter(e => new Date(e.date).getFullYear() === parseInt(yearFilter));
    }
    
    return filtered;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="transactions-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading expenses...</p>
        </div>
      </div>
    );
  }

  if (expenses.length === 0) {
    return (
      <div className="transactions-container">
        <div className="empty-state">
          <div className="empty-icon">💳</div>
          <h3>No Expenses Yet</h3>
          <p>Use "Add Expense" to record household expenses</p>
        </div>
      </div>
    );
  }

  const filteredExpenses = getFilteredExpenses();

  return (
    <div className="transactions-container">
      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Month:</label>
          <div className="month-year-selector">
            <button 
              className="arrow-btn"
              onClick={() => {
                if (monthFilter === 'all') {
                  setMonthFilter('11');
                } else {
                  const newMonth = parseInt(monthFilter) - 1;
                  if (newMonth < 0) {
                    setMonthFilter('11');
                    if (yearFilter !== 'all') {
                      setYearFilter((parseInt(yearFilter) - 1).toString());
                    }
                  } else {
                    setMonthFilter(newMonth.toString());
                  }
                }
              }}
            >
              ◀
            </button>
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              {months.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
            <button 
              className="arrow-btn"
              onClick={() => {
                if (monthFilter === 'all') {
                  setMonthFilter('0');
                } else {
                  const newMonth = parseInt(monthFilter) + 1;
                  if (newMonth > 11) {
                    setMonthFilter('0');
                    if (yearFilter !== 'all') {
                      setYearFilter((parseInt(yearFilter) + 1).toString());
                    }
                  } else {
                    setMonthFilter(newMonth.toString());
                  }
                }
              }}
            >
              ▶
            </button>
          </div>
        </div>

        <div className="filter-group">
          <label>Year:</label>
          <div className="month-year-selector">
            <button 
              className="arrow-btn"
              onClick={() => {
                if (yearFilter === 'all') {
                  setYearFilter(new Date().getFullYear().toString());
                } else {
                  setYearFilter((parseInt(yearFilter) - 1).toString());
                }
              }}
            >
              ◀
            </button>
            <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
              <option value="all">All Years</option>
              {getAvailableYears().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button 
              className="arrow-btn"
              onClick={() => {
                if (yearFilter === 'all') {
                  setYearFilter(new Date().getFullYear().toString());
                } else {
                  setYearFilter((parseInt(yearFilter) + 1).toString());
                }
              }}
            >
              ▶
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="transactions-table-wrapper">
        <table className="transactions-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Category</th>
              <th>Description</th>
              <th className="amount-col">Amount</th>
              <th>Paid By</th>
              <th>Notes</th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredExpenses.map(expense => (
              <tr key={expense.display_id} className={`transaction-row ${expense.expense_type}`}>
                <td className="date-col">
                  {formatDate(expense.date)}
                </td>
                <td className="type-col">
                  <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                    {expense.expense_type === 'household' ? (
                      <span className="transaction-type household" title="Expense with splits">
                        👥 Split
                      </span>
                    ) : (
                      <span className="transaction-type personal" title="Personal expense">
                        👤 Personal
                      </span>
                    )}
                    {expense.recurring_id && (
                      <span className="recurring-badge" title={`Recurring payment set by ${expense.recurring_creator || 'Unknown'}`}>
                        🔄 Recurring
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="category-cell">
                    <div className="category-name">{expense.category}</div>
                    {expense.subcategory && (
                      <div className="subcategory">{expense.subcategory}</div>
                    )}
                    {expense.recurring_creator && (
                      <div className="recurring-creator" title="Recurring payment created by">
                        Set by: {expense.recurring_creator}
                      </div>
                    )}
                  </div>
                </td>
                <td className="description-col">{expense.description}</td>
                <td className="amount-col expense">
                  {formatCurrency(expense.amount, expense.currency)}
                </td>
                <td className="paid-by-col">
                  <span className="paid-by-badge">
                    {expense.paid_by_name || 'Unknown'}
                  </span>
                </td>
                <td className="notes-col">
                  {expense.notes && (
                    <span className="notes-preview" title={expense.notes}>
                      {expense.notes.substring(0, 30)}
                      {expense.notes.length > 30 ? '...' : ''}
                    </span>
                  )}
                </td>
                <td className="actions-col">
                  {expense.recurring_id && (
                    <button 
                      className="recurring-btn"
                      onClick={() => handleViewRecurring(expense)}
                      title="View recurring transaction details"
                      style={{marginRight: '8px'}}
                    >
                      🔄
                    </button>
                  )}
                  <button 
                    className="delete-btn"
                    onClick={() => handleDelete(expense)}
                    title="Delete expense"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="transaction-summary">
        <div className="summary-item">
          <span className="summary-label">Total:</span>
          <span className="summary-amount expense">
            {formatCurrency(
              filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0),
              filteredExpenses[0]?.currency || 'EUR'
            )}
          </span>
        </div>
      </div>

      {/* Recurring Transaction Details Modal */}
      {showRecurringModal && selectedRecurring && (
        <div className="modal-overlay" onClick={() => setShowRecurringModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔄 Recurring Transaction Details</h2>
              <button className="close-btn" onClick={() => setShowRecurringModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="recurring-details-grid">
                <div className="detail-item">
                  <label>Description:</label>
                  <div className="detail-value">{selectedRecurring.description}</div>
                </div>

                <div className="detail-item">
                  <label>Category:</label>
                  <div className="detail-value">
                    {selectedRecurring.category}
                    {selectedRecurring.subcategory && ` • ${selectedRecurring.subcategory}`}
                  </div>
                </div>

                <div className="detail-item">
                  <label>Amount:</label>
                  <div className="detail-value amount">
                    {formatCurrency(selectedRecurring.amount, selectedRecurring.currency || 'GBP')}
                  </div>
                </div>

                <div className="detail-item">
                  <label>Frequency:</label>
                  <div className="detail-value">{selectedRecurring.frequency}</div>
                </div>

                <div className="detail-item">
                  <label>Start Date:</label>
                  <div className="detail-value">{formatDate(selectedRecurring.start_date)}</div>
                </div>

                {selectedRecurring.end_date && (
                  <div className="detail-item">
                    <label>End Date:</label>
                    <div className="detail-value">{formatDate(selectedRecurring.end_date)}</div>
                  </div>
                )}

                <div className="detail-item">
                  <label>Status:</label>
                  <div className="detail-value">
                    <span className={`status-badge ${selectedRecurring.is_active ? 'active' : 'inactive'}`}>
                      {selectedRecurring.is_active ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </div>
                </div>

                {selectedRecurring.last_generated && (
                  <div className="detail-item">
                    <label>Last Generated:</label>
                    <div className="detail-value">{formatDate(selectedRecurring.last_generated)}</div>
                  </div>
                )}

                {selectedRecurring.split_config && (
                  <div className="detail-item full-width">
                    <label>Split Configuration:</label>
                    <div className="detail-value">
                      {JSON.parse(selectedRecurring.split_config).map((split, idx) => (
                        <div key={idx} className="split-info">
                          User ID {split.user_id}: {formatCurrency(split.split_amount, selectedRecurring.currency || 'GBP')}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowRecurringModal(false)}>
                Close
              </button>
              <button className="btn-primary" onClick={handleEditRecurring}>
                Edit Recurring Transaction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpenseHistoryTable;
